// pages/WaitlistPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CheckCircle, Users, Clock, Zap, Shield, Star, ArrowRight } from 'lucide-react';
import NavBar from '../components/NavBar';
import ResponsiveFooter from '../components/ResponsiveFooter';
import { API_URL } from '../utils/apiConfig';

// Consistent color palette matching the rest of the site
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  successLight: 'rgba(52, 199, 89, 0.1)',
};

// Consistent font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const WaitlistPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    companyName: '',
    jobTitle: '',
    useCase: '',
    source: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setQueuePosition(data.data.queuePosition);
        toast.success('Successfully joined the waitlist! Check your email for confirmation.');
      } else if (response.status === 409) {
        setQueuePosition(data.data?.queuePosition);
        setIsSubmitted(true);
        toast.success('You\'re already on our waitlist! Check your email.');
      } else {
        throw new Error(data.message || 'Failed to join waitlist');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white text-gray-900" style={{ 
        color: colors.gray900,
        fontFamily: fontStyles.fontFamily 
      }}>
        <NavBar />
        <div className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-3xl p-12 border shadow-lg" style={{
              borderColor: colors.gray200,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)'
            }}>
              <div className="mb-12">
                <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-8" style={{
                  backgroundColor: colors.successLight
                }}>
                  <CheckCircle className="w-10 h-10" style={{ color: colors.success }} />
                </div>
                <h1 className="text-5xl mb-6" style={{
                  color: colors.gray900,
                  ...fontStyles.heading
                }}>
                  Welcome to the Future! 🎉
                </h1>
                <div className="mb-8">
                  <div className="inline-flex items-center px-8 py-4 rounded-full text-xl font-semibold mb-6" style={{
                    backgroundColor: colors.accent,
                    color: colors.white,
                    ...fontStyles.medium
                  }}>
                    🎯 You're #{queuePosition} in line!
                  </div>
                </div>
                <p className="text-xl mb-8" style={{
                  color: colors.gray700,
                  ...fontStyles.body
                }}>
                  You're now part of an exclusive group that will get first access to our revolutionary contract generation technology.
                </p>
              </div>

              <div className="rounded-2xl p-8 mb-8 border" style={{
                backgroundColor: colors.accentLight,
                borderColor: colors.gray200
              }}>
                <h3 className="text-xl font-semibold mb-3" style={{
                  color: colors.gray900,
                  ...fontStyles.heading
                }}>
                  📧 Check Your Email
                </h3>
                <p style={{
                  color: colors.gray700,
                  ...fontStyles.body
                }}>
                  We've sent you a confirmation email with details about your position and how to get priority access!
                </p>
              </div>

              <div className="rounded-2xl p-8 mb-8 border" style={{
                backgroundColor: colors.successLight,
                borderColor: colors.success + '40'
              }}>
                <h3 className="text-xl font-semibold mb-3" style={{
                  color: colors.gray900,
                  ...fontStyles.heading
                }}>
                  ⚡ Want Priority Access?
                </h3>
                <p className="mb-6" style={{
                  color: colors.gray700,
                  ...fontStyles.body
                }}>
                  Skip the wait and get immediate access to our e-signature platform for just $20/month. You'll be first in line for contract generation!
                </p>
                <button
                  onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank')}
                  className="inline-flex items-center px-6 py-3 rounded-xl text-white font-semibold transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: colors.success,
                    ...fontStyles.medium
                  }}
                >
                  Get Priority Access <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8 text-left">
                <div className="rounded-xl p-6 border" style={{
                  backgroundColor: colors.gray50,
                  borderColor: colors.gray200
                }}>
                  <Zap className="w-8 h-8 mb-3" style={{ color: colors.accent }} />
                  <h4 className="font-semibold mb-2" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>Instant Generation</h4>
                  <p style={{
                    color: colors.gray700,
                    ...fontStyles.body,
                    fontSize: '14px'
                  }}>Create professional contracts in seconds</p>
                </div>
                <div className="rounded-xl p-6 border" style={{
                  backgroundColor: colors.gray50,
                  borderColor: colors.gray200
                }}>
                  <Star className="w-8 h-8 mb-3" style={{ color: colors.accent }} />
                  <h4 className="font-semibold mb-2" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>AI-Powered</h4>
                  <p style={{
                    color: colors.gray700,
                    ...fontStyles.body,
                    fontSize: '14px'
                  }}>Smart suggestions and risk analysis</p>
                </div>
                <div className="rounded-xl p-6 border" style={{
                  backgroundColor: colors.gray50,
                  borderColor: colors.gray200
                }}>
                  <Users className="w-8 h-8 mb-3" style={{ color: colors.accent }} />
                  <h4 className="font-semibold mb-2" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>Interactive Redlining</h4>
                  <p style={{
                    color: colors.gray700,
                    ...fontStyles.body,
                    fontSize: '14px'
                  }}>Collaborative contract editing</p>
                </div>
                <div className="rounded-xl p-6 border" style={{
                  backgroundColor: colors.gray50,
                  borderColor: colors.gray200
                }}>
                  <Shield className="w-8 h-8 mb-3" style={{ color: colors.accent }} />
                  <h4 className="font-semibold mb-2" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>Enterprise Security</h4>
                  <p style={{
                    color: colors.gray700,
                    ...fontStyles.body,
                    fontSize: '14px'
                  }}>Bank-level encryption and compliance</p>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-4 px-8 rounded-xl font-semibold transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: colors.accent,
                    color: colors.white,
                    ...fontStyles.medium
                  }}
                >
                  Explore SignPact
                </button>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="w-full py-4 px-8 rounded-xl font-semibold transition-all duration-200 border hover:scale-105"
                  style={{
                    backgroundColor: colors.white,
                    color: colors.gray700,
                    borderColor: colors.gray300,
                    ...fontStyles.medium
                  }}
                >
                  Join Another Email
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        <ResponsiveFooter />
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ 
      color: colors.gray900,
      fontFamily: fontStyles.fontFamily 
    }}>
      <NavBar />
      <motion.div 
        className="pt-32 pb-24 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          background: `radial-gradient(circle at 30% 20%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 50%)`
        }}
      >
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <motion.div 
            className="text-center mb-16"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className="text-6xl md:text-7xl mb-8 leading-tight" style={{
              color: colors.gray900,
              ...fontStyles.heading
            }}>
              The future of contracts
              <span className="block" style={{ color: colors.accent }}>
                is almost here
              </span>
            </h1>
            <p className="text-xl mb-12 max-w-3xl mx-auto" style={{
              color: colors.gray700,
              ...fontStyles.body
            }}>
              Be among the first to experience AI-powered contract generation. Join our exclusive waitlist and get early access to the most advanced legal technology platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <div className="inline-flex items-center px-6 py-3 rounded-full border" style={{
                backgroundColor: colors.accentLight,
                color: colors.accent,
                borderColor: colors.accent + '40',
                ...fontStyles.medium
              }}>
                <Clock className="w-4 h-4 mr-2" />
                Rolling out within the next coming weeks
              </div>
              <div className="inline-flex items-center px-6 py-3 rounded-full border" style={{
                backgroundColor: colors.gray50,
                color: colors.gray700,
                borderColor: colors.gray300,
                ...fontStyles.medium
              }}>
                <Users className="w-4 h-4 mr-2" />
                85+ people in line
              </div>
            </div>
          </motion.div>

          {/* Waitlist Form */}
          <motion.div 
            className="bg-white rounded-3xl p-8 md:p-12 border shadow-lg"
            style={{
              borderColor: colors.gray200,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)'
            }}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                    placeholder="Acme Corp"
                  />
                </div>
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    Job Title
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                    placeholder="Legal Counsel"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="useCase" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    Primary Use Case
                  </label>
                  <select
                    id="useCase"
                    name="useCase"
                    value={formData.useCase}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                  >
                    <option value="">Select use case</option>
                    <option value="Legal">Legal</option>
                    <option value="HR">HR</option>
                    <option value="Sales">Sales</option>
                    <option value="Personal">Personal</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="source" className="block text-sm font-medium mb-3" style={{
                    color: colors.gray900,
                    ...fontStyles.medium
                  }}>
                    How did you hear about us?
                  </label>
                  <input
                    type="text"
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{
                      backgroundColor: colors.gray50,
                      borderColor: colors.gray300,
                      color: colors.gray900,
                      ...fontStyles.body,
                      '--tw-ring-color': colors.accent
                    }}
                    placeholder="Google, LinkedIn, etc."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 px-8 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  backgroundColor: isSubmitting ? colors.gray500 : colors.accent,
                  color: colors.white,
                  ...fontStyles.medium
                }}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Joining Waitlist...
                  </div>
                ) : (
                  <span className="flex items-center justify-center">
                    Join the Waitlist <ArrowRight className="ml-2 w-5 h-5" />
                  </span>
                )}
              </button>

              <p className="text-center text-sm" style={{
                color: colors.gray500,
                ...fontStyles.body
              }}>
                Join 85+ professionals already in line for early access
              </p>
            </form>
          </motion.div>

          {/* Features Preview */}
          <motion.div 
            className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <div className="rounded-2xl p-8 border" style={{
              backgroundColor: colors.gray50,
              borderColor: colors.gray200
            }}>
              <Zap className="w-10 h-10 mb-4" style={{ color: colors.accent }} />
              <h3 className="font-semibold mb-3" style={{
                color: colors.gray900,
                ...fontStyles.medium
              }}>Instant Generation</h3>
              <p style={{
                color: colors.gray700,
                ...fontStyles.body,
                fontSize: '14px'
              }}>Create professional contracts in seconds using advanced AI</p>
            </div>
            <div className="rounded-2xl p-8 border" style={{
              backgroundColor: colors.gray50,
              borderColor: colors.gray200
            }}>
              <Star className="w-10 h-10 mb-4" style={{ color: colors.accent }} />
              <h3 className="font-semibold mb-3" style={{
                color: colors.gray900,
                ...fontStyles.medium
              }}>AI-Powered Insights</h3>
              <p style={{
                color: colors.gray700,
                ...fontStyles.body,
                fontSize: '14px'
              }}>Get smart suggestions and automated risk analysis</p>
            </div>
            <div className="rounded-2xl p-8 border" style={{
              backgroundColor: colors.gray50,
              borderColor: colors.gray200
            }}>
              <Users className="w-10 h-10 mb-4" style={{ color: colors.accent }} />
              <h3 className="font-semibold mb-3" style={{
                color: colors.gray900,
                ...fontStyles.medium
              }}>Interactive Redlining</h3>
              <p style={{
                color: colors.gray700,
                ...fontStyles.body,
                fontSize: '14px'
              }}>Collaborate in real-time with advanced editing tools</p>
            </div>
            <div className="rounded-2xl p-8 border" style={{
              backgroundColor: colors.gray50,
              borderColor: colors.gray200
            }}>
              <Shield className="w-10 h-10 mb-4" style={{ color: colors.accent }} />
              <h3 className="font-semibold mb-3" style={{
                color: colors.gray900,
                ...fontStyles.medium
              }}>Enterprise Security</h3>
              <p style={{
                color: colors.gray700,
                ...fontStyles.body,
                fontSize: '14px'
              }}>Bank-level encryption and compliance-ready audit trails</p>
            </div>
          </motion.div>
        </div>
      </motion.div>
      <ResponsiveFooter />
      <Toaster position="top-center" />
    </div>
  );
};

export default WaitlistPage;